interface IndicatorCardProps {
  label: string;
  value: string | number;
  unit?: string;
  icon?: React.ReactNode;
  /** Existing API — kept for the PlanCanicule / Renovation screens. */
  trend?: 'up' | 'down' | 'stable';
  /** Existing API — overrides the value colour. */
  color?: string;
  /** Hi-fi dashboard: small delta badge (e.g. "+29,9 %"). */
  delta?: string;
  deltaTone?: 'up' | 'down' | 'neutral';
  /** Contextual note shown next to the delta badge (e.g. source / year). */
  deltaNote?: string;
  /** When true, the value is being computed (ingestion in progress): muted + pulse. */
  loading?: boolean;
}

const DELTA_TONE: Record<'up' | 'down' | 'neutral', { color: string; bg: string }> = {
  up: { color: '#1f8a5b', bg: '#eaf6ef' },
  down: { color: '#c2410c', bg: '#fff1ea' },
  neutral: { color: '#5b6480', bg: '#f1f4f9' },
};

const TREND: Record<'up' | 'down' | 'stable', { text: string; color: string }> = {
  up: { text: '↑ En hausse', color: '#1f8a5b' },
  down: { text: '↓ En baisse', color: '#c2410c' },
  stable: { text: '→ Stable', color: '#6b7392' },
};

export function IndicatorCard({
  label,
  value,
  unit,
  icon,
  trend,
  color,
  delta,
  deltaTone = 'neutral',
  deltaNote,
  loading = false,
}: IndicatorCardProps) {
  const tone = DELTA_TONE[deltaTone];

  return (
    <div className="rounded-card border border-line bg-white p-4 shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">
          {label}
        </span>
        {icon && (
          <span className="text-muted" style={color ? { color } : undefined} aria-hidden="true">
            {icon}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-baseline gap-1">
        <span
          className={`font-display text-2xl font-extrabold tracking-tight${
            loading ? ' animate-pulse text-faint' : ''
          }`}
          style={loading ? undefined : { color: color ?? '#11193a' }}
        >
          {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
        </span>
        {unit && <span className="text-xs font-medium text-faint">{unit}</span>}
      </div>

      {delta ? (
        <div className="mt-1.5 flex items-center gap-1.5">
          <span
            className="rounded-md px-1.5 py-0.5 text-[11px] font-bold"
            style={{ color: tone.color, background: tone.bg }}
          >
            {delta}
          </span>
          {deltaNote && <span className="text-[11px] text-faint">{deltaNote}</span>}
        </div>
      ) : (
        trend && (
          <p className="mt-1.5 text-[11.5px] font-semibold" style={{ color: TREND[trend].color }}>
            {TREND[trend].text}
          </p>
        )
      )}
    </div>
  );
}
